import { NavLink } from "react-router-dom";
import { Download, Send } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { csvName, downloadCsv } from "../../../../lib/csv";
import { Card, CardHead, EmptyState, IconBtn, fmtDate, fmtNum, fmtPct, listCard } from "../../../ui";
import type { DashCampaign } from "../../../../lib/dashboard";

const COLS = "minmax(0,1.5fr) .7fr .95fr .95fr .7fr";

/** A rate with its own bar. `of` is the send count, so an empty campaign shows
 *  a dash rather than a division by zero dressed up as 0%. */
function Rate({ n, of, color }: { n: number; of: number; color: string }) {
  if (!of) return <span className="crm-mut text-[13px]">-</span>;
  const pct = (n / of) * 100;
  return (
    <span>
      <span className="crm-rate-v">{fmtPct(pct)}</span>
      <span className="crm-rate-b">
        <i style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </span>
    </span>
  );
}

/**
 * The last four campaigns that actually sent something.
 *
 * The columns are what this platform can honestly measure: delivered, replies,
 * bounces, opt-outs. There is no open or click rate here because outreach mail
 * carries no tracking pixel and no rewritten links - adding the columns and
 * filling them with zeroes would read as a broken campaign rather than as a
 * deliberate absence.
 */
export function CampaignsCard({ campaigns }: { campaigns: DashCampaign[] }) {
  const t = useT();

  // Counts, not the percentages on screen. A reply rate can be recomputed from
  // sends and replies; sends and replies cannot be recovered from a rate, and
  // the point of an export is to be able to do arithmetic somewhere else.
  const exportCsv = () =>
    downloadCsv(csvName("campaigns", new Date()), [
      [
        t("dash.campaigns.name"),
        t("dash.campaigns.sent"),
        t("dash.campaigns.replied"),
        t("dash.campaigns.bounced"),
        t("dash.campaigns.optout"),
      ],
      ...campaigns.map((c) => [c.name, c.sent, c.replied, c.bounced, c.unsubscribed]),
    ]);

  return (
    <Card>
      <CardHead
        title={t("dash.campaigns.title")}
        sub={t("dash.campaigns.sub")}
        actions={
          campaigns.length ? (
            <IconBtn onClick={exportCsv} title={t("dash.export")} aria-label={t("dash.export")}>
              <Download className="size-4" />
            </IconBtn>
          ) : null
        }
      />
      {campaigns.length === 0 ? (
        <EmptyState icon={Send} title={t("dash.campaigns.emptyTitle")} body={t("dash.campaigns.emptyBody")} />
      ) : (
        <>
          <div className="crm-grid-row crm-grid-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS }}>
            <span>{t("dash.campaigns.name")}</span>
            <span>{t("dash.campaigns.sent")}</span>
            <span>{t("dash.campaigns.replied")}</span>
            <span>{t("dash.campaigns.bounced")}</span>
            <span>{t("dash.campaigns.optout")}</span>
          </div>
          <div className="pb-3.5">
            {campaigns.map((c) => (
              <NavLink
                key={c.id}
                to={`/marketing/campaigns/${c.id}`}
                className="crm-grid-row crm-grid-body"
                style={{ ...listCard(COLS), gridTemplateColumns: COLS }}
              >
                <span className="min-w-0">
                  <span className="crm-prow-n crm-trunc block">{c.name}</span>
                  <span className="crm-prow-s block">
                    {c.audience === "scanLeads" ? t("marketing.source.scanLeads") : t("marketing.source.register")}{" "}
                    &middot; {fmtDate(c.at)}
                  </span>
                </span>
                <span className="crm-tnum text-[13px] font-[650]">{fmtNum(c.sent)}</span>
                <Rate n={c.replied} of={c.sent} color="var(--color-primary)" />
                <Rate n={c.bounced} of={c.sent} color="var(--color-warning)" />
                <span className="crm-tnum text-[13px] font-bold">{fmtNum(c.unsubscribed)}</span>
              </NavLink>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
